"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, FileText, Layers, ShieldCheck, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const keySpecs = [{
  icon: Activity,
  label: "Spectral Range",
  value: "950 – 1650 nm",
  detail: "High signal-to-noise ratio"
}, {
  icon: Zap,
  label: "Wireless Operation",
  value: "Integrated Battery & Wi-Fi",
  detail: "Direct tumble blender mount"
}, {
  icon: ShieldCheck,
  label: "ATEX / IECEx",
  value: "Zone 0 / 20 Certified",
  detail: "Hazardous area compliance"
}, {
  icon: Layers,
  label: "PAT Integration",
  value: "OPC UA & CSV Output",
  detail: "Direct control system linkage"
}];
const highlights = ["Non-invasive continuous blend uniformity monitoring", "Real-time drying endpoint detection for fluid beds", "Designed for pharmaceutical and fine chemical manufacturing", "Complete qualification, IQ/OQ, and local support across UK & Ireland"];
export default function MicroNIR() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        duration: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="heromicronirpatwx" data-nav="light" className="relative bg-background text-foreground py-12 md:py-20 lg:py-24 overflow-hidden border-b border-border">
      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Main Content Column */}
          <motion.div className="lg:col-span-7 flex flex-col items-start" variants={containerVariants} initial="hidden" animate="visible">
            <motion.div variants={itemVariants} className="mb-4">
              <Badge variant="secondary" className="px-3 py-1 gap-1.5 text-xs font-medium">
                <Cpu className="size-3.5 text-primary" />
                Process Analytical Technology
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground leading-tight mb-5">
              MicroNIR PAT-Wx Wireless NIR Process Spectrometer
            </motion.h1>

            <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6 max-w-2xl">
              Compact, hazardous-area certified near-infrared spectrometer engineered for
              real-time process monitoring in rotating blenders, fluid bed dryers, and continuous
              manufacturing lines.
            </motion.p>

            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full mb-8">
              {highlights.map((point, index) => <div key={index} data-index={index} className="flex items-start gap-2 text-sm text-foreground/90">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>{point}</span>
                </div>)}
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-3.5 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/BookADemoPage">
                  Book a Demonstration
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/DiscussYourApplicationPage">
                  Discuss Your Application
                </Link>
              </Button>

              <Button asChild variant="ghost" size="lg" className="w-full sm:w-auto">
                <Link to="/ProcessAnalyticalTechnologyPage">
                  <FileText className="size-4 text-muted-foreground" />
                  Technical Specs
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Product Media & Quick Summary Card */}
          <motion.div className="lg:col-span-5" initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }}>
            <div className="bg-card text-card-foreground border border-border rounded-xl shadow-md overflow-hidden">
              <div className="relative aspect-4/3 bg-muted/40 p-6 flex items-center justify-center border-b border-border overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_wx_spectrometer_device.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_wx_spectrometer_device.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_wx_spectrometer_device.png-wvc-sizes" alt="MicroNIR PAT-Wx Wireless Process Spectrometer" loading="lazy" decoding="async" className="w-full h-full object-contain object-center" />
                <div className="absolute top-3 right-3">
                  <Badge variant="outline" className="bg-background/90 backdrop-blur-xs text-xs font-mono">
                    IP67 & ATEX
                  </Badge>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-base font-default font-semibold text-foreground">
                      Antech Qualification & Support
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Regulated pharmaceutical & industrial deployment
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    Ireland & UK
                  </Badge>
                </div>

                <Separator className="my-3" />

                <div className="grid grid-cols-2 gap-3 pt-1">
                  {keySpecs.map((spec, index) => {
                  const IconComponent = spec.icon;
                  return <div key={index} data-index={index} className="bg-muted/40 rounded-lg p-2.5 border border-border/60 flex flex-col justify-between">
                        <div className="flex items-center gap-1.5 mb-1 text-muted-foreground">
                          <IconComponent className="size-3.5 text-primary shrink-0" />
                          <span className="text-xs font-medium truncate">{spec.label}</span>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-foreground">
                            {spec.value}
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {spec.detail}
                          </div>
                        </div>
                      </div>;
                })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}